import { Component } from "@kluntje/core";
import { html, render } from "lit-html";
import { I18nService } from "Services/I18nService";
import { onEvent } from "@kluntje/js-utils/lib/dom-helpers";
import { scrollLock } from "Helper/domHelper";
import { autobind } from "Helper/functionHelper/decorators";
import { createRef, ref } from "Helper/litHelpers";
import MediaQueryService from "Services/MediaQueryService";
import { MQ } from "Services/MediaQueryService";

import FraTimePickerField from "./fra-e-time-picker__field";
import css from "./fra-e-time-picker.shadow.scss";

interface FraTimePickerState {
  open: boolean;
  currentTime: string;
  selectedTime: string;
}

interface FraTimeObject {
  value: string;
  selected: boolean;
}

class FraTimePicker extends Component {
  /*
   * returns a list of hours in a day, values between 00 and 23
   */
  private getHours: FraTimeObject[] = Array.from({ length: 24 }, (_, ix) => ({
    value: ix.toString().padStart(2, "0"),
    selected: ix.toString().padStart(2, "0") === "00",
  }));

  /*
   * returns a list of minutes with 15 min interval, values are 0, 15, 30, 45
   */
  private getMinutes: FraTimeObject[] = Array.from({ length: 4 }, (_, ix) => ({
    value: (ix * 15).toString().padStart(2, "0"),
    selected: (ix * 15).toString().padStart(2, "0") === "00",
  }));

  /*
   * tracks if the last user interaction was tab navigation, (false when last interaction was a click)
   */
  private _hasTabNavigated = false;
  private _fullVpDialogOpened = false;

  constructor() {
    super({
      useShadowDOM: true,
      reactions: {
        "*": ["render"],
        open: ["updateGlobalEventListeners", "togglePageScrollLock"],
        selectedTime: ["updateDisplayValues"],
      },
      initialStates: {
        open: false,
        currentTime: "00:00",
        selectedTime: "00:00",
        selectedHour: "00",
        selectedMinute: "00",
      },
      events: [
        {
          event: "fra-mq-change",
          target: "window",
          handler: "onMQChange",
        },
      ],
    });
  }

  refs = {
    timePickerDialog: createRef<HTMLDivElement>(),
  };

  // @override
  afterComponentRender() {
    // Component event doesn't support event binding to light dom content
    onEvent(this.timePickerContainer, "click", this.open, this);
    onEvent(this.timePickerContainer, "focus", this.open, this);
  }

  get fields() {
    return this.querySelectorAll<FraTimePickerField>("fra-e-time-picker__field:not([disabled])");
  }

  get timePickerContainer() {
    return this.querySelector(".fra-e-time-picker__container");
  }

  updateGlobalEventListeners({ open }: FraTimePickerState) {
    if (open) {
      window.addEventListener("keydown", this.handleKeypress);
      if (MediaQueryService.currentMQ <= "MQ2") {
        // allow closing the dialog with the back button (a pattern used on mobile devices)
        window.history.pushState({}, "");
        window.addEventListener("popstate", this.close);
      }
    } else {
      window.removeEventListener("keydown", this.handleKeypress);
      window.removeEventListener("popstate", this.close);
    }
  }

  // prevent user to scroll the content when the fullview dialog is open on mobile
  togglePageScrollLock({ open }: FraTimePickerState) {
    if (open && MediaQueryService.currentMQ <= "MQ2") {
      this._fullVpDialogOpened = true;
      scrollLock.pageBodyLock();
    }
    // closing mobile dialog
    else if (this._fullVpDialogOpened) {
      scrollLock.pageBodyUnlock();
      this._fullVpDialogOpened = false;
    }
  }

  /**
   * Handle MQ change while menu is active
   */
  onMQChange({ detail: { oldMQ, newMQ } }: { detail: { oldMQ: MQ; newMQ: MQ } }): void {
    if (newMQ >= "MQ3" && oldMQ <= "MQ2") {
      // new viewport size requires no scroll lock
      scrollLock.pageBodyUnlock();
    }
    // for vieport change to MQ1-MQ2 toggle scrolllock
    this.togglePageScrollLock(this.state);
  }

  /*
   * shows the current selection in the date fields, without updating the "value"
   */
  updateDisplayValues({ currentTime }: FraTimePickerState) {
    if (!currentTime) return "";
    this.fields[0].displayValue = currentTime;
  }

  @autobind
  handleKeypress(e: KeyboardEvent) {
    const focusEl = this.shadowRoot!.activeElement as HTMLElement | null;

    if (e.code === "Escape") {
      // remove focus on first escape after a element was tab navigated to
      if (focusEl && this._hasTabNavigated) {
        focusEl.blur();
      }
      // close dialog on escape
      else {
        this.close(e);
      }
    } else if (e.code === "Tab") {
      this._hasTabNavigated = true;
    } else if (e.code === "Enter") {
      if (!this._hasTabNavigated || !focusEl?.matches(this.focusableChildSelectors)) {
        e.preventDefault();

        if (this.validity.valid) {
          this.saveChanges(e);
        }
      }
    }
  }

  /*
   * check if selectedTime is different from presetTime; update current time and display value accordingly
   * close the dialog after changes are saved
   */
  @autobind
  saveChanges(e: Event) {
    const { selectedTime } = this.state;
    const presetTime = `${this.state.selectedHour}:${this.state.selectedMinute}`;
    if (!selectedTime || !presetTime) return;

    if (selectedTime !== presetTime && !(MediaQueryService.currentMQ <= "MQ2")) {
      this.setState({ currentTime: presetTime, selectedTime: presetTime });
    } else {
      this.setState({ currentTime: selectedTime });
    }
    this.value = this.state.currentTime;

    this.close(e);
    this.dispatchEvent(new CustomEvent("fra-e-time-picker:change"));
  }

  @autobind
  close(e: Event) {
    if (e.type !== "popstate" && this._fullVpDialogOpened) window.history.back();
    this.setState({ open: false });
    window.dispatchEvent(new CustomEvent("fra-time-picker-dialog", { detail: { active: false } }));
  }

  open() {
    // even when the user has opened the UI via tab, he hasn't tabbed inside the UI yet!
    this._hasTabNavigated = false;
    const currentTime = this.value;

    this.setState({
      open: true,
      selectedTime: currentTime,
      currentTime,
    });

    const [hh, mm] = currentTime.split(":");
    this.setHours(hh);
    this.initMin(mm);

    // if dialog opened out of view, scroll to center it
    const dialogEl = this.refs.timePickerDialog.value!;
    const { bottom, top } = dialogEl.getBoundingClientRect();
    if (bottom > window.innerHeight || top < 0) {
      setTimeout(() => dialogEl.scrollIntoView({ block: "center", behavior: "smooth" }), 0);
    }
    window.dispatchEvent(new CustomEvent("fra-time-picker-dialog", { detail: { active: true } }));
  }

  /*
   * Find the previously selected hour entry and reset
   * Handle the case where the hour exceeds 23 by resetting it to 0
   * Update the state with the selected hour value
   */
  private setHours(h: string) {
    this.resetList(this.getHours);

    // fallback when user enters text input
    let hour = parseInt(h, 10) || 0;
    if (hour > 23) hour = 0;

    const hh = hour?.toString().padStart(2, "0");
    const selectedHour = this.getHours.find(({ value }) => value === hh)!.value;
    this.setState({ selectedHour });
    this.getHours.find(({ value }) => value === hh)!.selected = true;
    return selectedHour;
  }

  private resetList(options: Array<{ value: string; selected: boolean }>) {
    const activeEntry = options.find(({ selected }) => selected);
    if (activeEntry) activeEntry.selected = false;
  }

  private initMin(m: string) {
    this.resetList(this.getMinutes);
    // determine the closest lower multiple of 15 based on the current minute
    const roundDown = Math.floor(parseInt(m, 10) / 15) * 15;
    const mm = roundDown?.toString().padStart(2, "0");
    const findEntry = this.getMinutes.find(({ value }) => value === mm);
    findEntry!.selected = true;
    return this.setState({ selectedMinute: findEntry!.value });
  }

  /*
   * Find the previously selected minute entry and reset
   * Handle the case where the hour exceeds 60 by resetting it to 0 & Update the state
   */
  private setMinutes(m: string) {
    this.resetList(this.getMinutes);

    // fallback when user enters text input
    let minute = parseInt(m, 10) || 0;
    if (minute >= 60) minute = 0;

    const mm = minute?.toString().padStart(2, "0");
    const findEntry = this.getMinutes.find(({ value }) => value === mm);
    let selectedMinute;
    if (findEntry) {
      findEntry.selected = true;
      selectedMinute = findEntry.value;
    } else {
      selectedMinute = mm;
    }
    this.setState({ selectedMinute });
    return selectedMinute;
  }

  /*
   * returns an array of time strings from '00:00' to '23:45' in 15' minutes steps
   * [time="00:00"] - current selected time, from the components state. in the "hh:mm" format
   */
  private getTimeOptions(time = "00:00") {
    const options = [];
    const hhmm = (num: number) => String(num).padStart(2, "0");

    for (let i = 0; i < 24; i++) {
      for (let j = 0; j <= 45; j += 15) {
        if (i === 0 && j === 0) continue;

        const value = `${hhmm(i)}:${hhmm(j)}`;
        const text = html`
          ${hhmm(i)}&emsp;:&emsp;${hhmm(j)}
        `;

        options.push({ value, text, selected: value === time });
      }
    }

    return options;
  }

  private onInputChange(e: Event) {
    const target = e.target as HTMLInputElement;
    return target.value;
  }

  private decrementTime(options: Array<{ value: string; selected: boolean }>, activeIndex: number) {
    const prevValue = options[(activeIndex - 1 + options.length) % options.length].value;
    return prevValue;
  }

  private incrementTime(options: Array<{ value: string; selected: boolean }>, activeIndex: number) {
    const nextValue = options[(activeIndex + 1) % options.length].value;
    return nextValue;
  }

  private handleKeydown(e: KeyboardEvent, options: Array<{ value: string; selected: boolean }>, activeIndex: number) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      return this.incrementTime(options, activeIndex);
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      return this.decrementTime(options, activeIndex);
    }
  }

  private timeInput({
    options,
    value,
    onChange,
    callback,
    max,
  }: {
    options: Array<{ value: string; selected: boolean }>;
    value: string;
    onChange: Function;
    callback: Function;
    max: number;
  }) {
    const activeIndex = Math.max(
      options.findIndex(el => el.value === value),
      0,
    );

    return html`
      <div class="fra-e-time-picker__time-value ">
        <button
          class="fra-e-time-picker__input-icon"
          type="button"
          aria-label="decrement"
          @click=${() => {
            const nextValue = this.decrementTime(options, activeIndex);
            callback(nextValue);
            onChange(nextValue);
          }}
        >
          <fra-e-icon class="fra-e-icon" icon-id="fra-icon-arrow"></fra-e-icon>
        </button>
        <input
          class="fra-e-time-picker__user-input"
          type="number"
          min="0"
          max="${max}"
          .value="${options.find(({ selected }) => selected === true)?.value || value}"
          pattern="d{2}"
          oninput="this.value = this.value.slice(0, 2)"
          @change="${(e: InputEvent) => {
            const targetvalue = (e.target as HTMLInputElement).value;
            const formatTime = callback(targetvalue);
            (e.target as HTMLInputElement).value = formatTime;
            onChange(this.onInputChange(e));
          }}"
          @keydown="${(e: KeyboardEvent) => {
            const value = this.handleKeydown(e, options, activeIndex);
            if (value) {
              callback(value);
              onChange(value);
            }
          }}"
        />
        <button
          class="fra-e-time-picker__input-icon fra-e-time-picker__input-icon-down"
          type="button"
          aria-label="increment"
          @click=${() => {
            const prevValue = this.incrementTime(options, activeIndex);
            callback(prevValue);
            onChange(prevValue);
          }}
        >
          <fra-e-icon class="fra-e-icon" icon-id="fra-icon-arrow"></fra-e-icon>
        </button>
      </div>
    `;
  }

  /*
   * returns markup for the time picker. ( hour and minute drop down)
   */
  private timePicker({
    label,
    time,
    onChange,
    error,
  }: {
    label: string;
    time: string;
    onChange: Function;
    error?: boolean;
  }) {
    const [hour, minute] = time.split(":");

    return html`
      <div class="fra-e-time-picker__fieldset ${error ? "fra-e-time-picker__time-fieldset--error" : ""}">
        <div class="fra-e-time-picker__clock">
          ${this.timeInput({
            options: this.getHours,
            value: this.state.selectedHour,
            max: 23,
            callback: (hh: string) => this.setHours(hh),
            onChange: (hh: string) => onChange(`${hh}:${minute}`),
          })}
          <span class="fra-e-time-picker__time-separator">:</span>
          ${this.timeInput({
            options: this.getMinutes,
            value: this.state.selectedMinute,
            max: 59,
            callback: (mm: string) => this.setMinutes(mm),
            onChange: (mm: string) => onChange(`${hour}:${mm}`),
          })}
        </div>
        <div class="fra-e-time-picker__mobile-clock">
          <select
            name="time picker"
            class="fra-e-time-picker__mobile-clock-select"
            @change=${(e: InputEvent) => onChange((e.currentTarget as HTMLSelectElement).value)}
          >
            ${this.getTimeOptions(time).map(
              ({ value, selected, text }) => html`
                <option class="fra-e-time-picker__mobile-clock-option" .selected=${selected} value=${value}>
                  ${text}
                </option>
              `,
            )}
          </select>
        </div>
      </div>
    `;
  }

  render() {
    const { open, selectedTime }: FraTimePickerState = this.state;

    if (!open) {
      render(
        html`
          <style>
            ${css}
          </style>
          <slot></slot>
        `,
        this.shadowRoot!,
      );
      return;
    }

    // assuming at this point the dictionary is loaded
    const applyI18n = I18nService.getInstance().getKey("fraport.travel.components.flightBooking.button.takeover.label");
    render(
      html`
        <style>
          ${css}
        </style>
        <slot></slot>
        <div part="back-drop" class="fra-e-time-picker__back-drop" @click=${this.close}></div>
        <div
          part="dialog"
          class="fra-e-time-picker__dialog ${!this.validity.valid ? "fra-e-time-picker__dialog--has-error" : ""}"
          role="application"
          aria-roledescription="timepicker"
          aria-label="Timepicker"
          @mousedown=${() => (this._hasTabNavigated = false)}
          ref=${ref(this.refs.timePickerDialog)}
        >
          <div class="fra-e-time-picker__content">
            <div class="fra-e-time-picker__time-wrapper">
              ${this.timePicker({
                label: this.fields[0].timeLabel,
                time: selectedTime,
                error: this.validity.invalidTime,
                onChange: (time: string) => this.setState({ selectedTime: time }),
              })}
            </div>
            <div class="fra-e-time-picker__error-msg ${this.validity.errorMessage ? "fra-state-active" : ""}">
              ${this.validity.errorMessage}
            </div>
            <button class="fra-e-time-picker__save-btn" @click=${this.saveChanges} ?disabled=${!this.validity.valid}>
              ${applyI18n}
            </button>
          </div>
        </div>
      `,
      this.shadowRoot!,
    );
  }

  /*
   * Get the value attribute of the time picker field
   */
  get value() {
    return this.fields[0].value || "";
  }

  set value(val) {
    if (val === undefined || val === null) return;
    this.fields[0].value = val;
  }

  /*
   * checks if the user has selected the required values for and time(s)
   * according to the current user story and usage, the current time is selected
   */
  get validity() {
    const { selectedTime } = this.state as FraTimePickerState;

    if (!selectedTime) {
      return {
        valid: false,
        invalidTime: true,
        errorMessage: I18nService.getInstance().getKey(
          "fraport.travel.components.global.timePicker.errorMessage.invalidTime",
        ),
      };
    }

    return {
      valid: true,
      errorMessage: "",
    };
  }
}
customElements.define("fra-e-time-picker", FraTimePicker);
export default FraTimePicker;
